import React from 'react';
import { Grid, Typography, useTheme } from '@mui/material';

import { sumarUnDia } from '@shared/helpers/formatDate';

import CloseButton from './shared/CloseButton';

import EntityInfo from './shared/EntityInfo';

import { GridMainContainer, sxStyle } from '../styles';
import { HEADER_MESSAGES } from '@shared/pages/Hotels/HotelBookingDone/Payment/constants';
import { useSelector } from 'react-redux';

const resolveValue = (value, context) =>
  typeof value === 'function' ? value(context) : value;

const STATUS_IDS = {
  CONFIRM: 1,
  CANCELLED: 2,
  PRE_BOOKING: 3,
  FAILED: 4,
  DENIED: 5,
  REQUIRES_REVIEW: 6,
};

const STATUS_LABELS = {
  [STATUS_IDS.CONFIRM]: 'CONFIRM',
  [STATUS_IDS.CANCELLED]: 'CANCELLED',
  [STATUS_IDS.PRE_BOOKING]: 'PRE-BOOKING',
  [STATUS_IDS.FAILED]: 'FAILED',
  [STATUS_IDS.DENIED]: 'DENIED',
  [STATUS_IDS.REQUIRES_REVIEW]: 'REQUIRES_REVIEW',
};

const STATUS_DESCRIPTIONS = {
  [STATUS_IDS.CONFIRM]: 'Confirmada',
  [STATUS_IDS.CANCELLED]: 'Cancelada',
  [STATUS_IDS.PRE_BOOKING]: 'Pre-reserva',
  [STATUS_IDS.FAILED]: 'Fallo',
  [STATUS_IDS.DENIED]: 'Denegada',
  [STATUS_IDS.REQUIRES_REVIEW]: 'Revisar',
};

// El pasajero no logueado nunca ve REQUIRES_REVIEW ni FAILED:
// ve el último estado exitoso, o DENEGADA si la reserva falló.
const GUEST_STATUS_TRANSFORMS = {
  [STATUS_IDS.REQUIRES_REVIEW]: bookingDetails =>
    bookingDetails?.is_b2b_booking && bookingDetails?.refundable
      ? STATUS_IDS.PRE_BOOKING
      : STATUS_IDS.CONFIRM,
  [STATUS_IDS.FAILED]: () => STATUS_IDS.DENIED,
};

const resolveGuestBookingStatus = (bookingStatus, bookingDetails) => {
  const transform = GUEST_STATUS_TRANSFORMS[bookingStatus];
  return transform ? transform(bookingDetails) : bookingStatus;
};

const Hotels = ({
  searchType,
  bookingDetails,
  useBookingColor,
  bookingStatus,
  business_types,
  formatDate,
  hasEntity,
  name,
  getHref,
  history,
  bookingLabel,
}) => {
  const theme = useTheme();

  const { id } = useSelector(store => store.pageConfiguration);
  const { userToken } = useSelector(store => store.userInfo);

  const isLoggedIn = Boolean(userToken);

  const displayBookingStatus = isLoggedIn
    ? bookingStatus
    : resolveGuestBookingStatus(bookingStatus, bookingDetails);

  const displayBookingLabel = isLoggedIn
    ? bookingLabel
    : STATUS_LABELS[displayBookingStatus] ?? bookingLabel;

  const displayBookingDescription = isLoggedIn
    ? bookingDetails?.booking_status?.description
    : STATUS_DESCRIPTIONS[displayBookingStatus] ??
      bookingDetails?.booking_status?.description;

  const operationType = displayBookingStatus === 1 ? 'PAYMENT' : 'BOOKING';

  const isSucursal = +id === 2;

  const siteType = isSucursal ? 'GIT' : 'TDH';

  const message = HEADER_MESSAGES[operationType][displayBookingStatus];

  const context = {
    isSucursal,
    site: siteType,
  };

  return (
    <GridMainContainer
      searchType={searchType}
      data-testid="bookingDetail_view_hotels"
    >
      <Grid container direction="column" alignItems="center" sx={sxStyle.root}>
        <Grid item sx={sxStyle.gridPadding}>
          <Grid item container direction="column" alignItems="center">
            <Typography
              align="center"
              sx={{ pb: 1, ...sxStyle.bookingDetailTitle }}
            >
              ¡Encontramos la reserva!
            </Typography>

            <Typography component="p">
              {resolveValue(message?.subtitle, context)}
            </Typography>

            {message?.showBookingData && bookingDetails?.booking_code && (
              <Typography sx={{ pt: 1, ...sxStyle.bookingDetailSubtitle }}>
                Código de reserva:{' '}
                <Typography component="span" sx={{ color: theme.tertiary }}>
                  {bookingDetails?.booking_code}
                </Typography>
              </Typography>
            )}
            <Typography sx={sxStyle.bookingDetailSubtitle}>
              Código de localizador: {bookingDetails?.locator}
            </Typography>

            <Grid container sx={sxStyle.gridContainer}>
              <Grid
                item
                sx={{
                  ...sxStyle.status,
                  backgroundColor: useBookingColor(displayBookingLabel),
                }}
              >
                Estado: {displayBookingDescription}
              </Grid>

              {displayBookingLabel === 'FAILED' && (
                <Grid
                  item
                  container
                  sx={{
                    border: '1px solid #636466',
                    borderTop: 'none',
                    borderBottom: 'none',
                  }}
                >
                  <Grid item xs={12} sx={sxStyle.gridItemBookingDetailHotel}>
                    {business_types.code === 'BTB' ? (
                      <Typography sx={sxStyle.bookingDetailText}>
                        <b>Falló la creación de la Reserva</b>. Comunicate con
                        tu vendedor de confianza para chequear el estado de la
                        reserva y volver a tomarla.
                      </Typography>
                    ) : (
                      <Typography sx={sxStyle.bookingDetailText}>
                        <b>Falló la creación de la Reserva</b>. Comunicate con
                        el {`${name}`} seleccionado para chequear el estado de
                        la reserva y volver a tomarla.
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              )}
              {displayBookingLabel === 'PRE-BOOKING' && (
                <Grid
                  item
                  xs={12}
                  sx={{
                    border: '1px solid #636466',
                    borderTop: 'none',
                    textAlign: 'center',
                    padding: '20px 50px',
                  }}
                >
                  {business_types.code === 'BTB' ? (
                    <Typography sx={sxStyle.bookingDetailText}>
                      Recordá{' '}
                      <b>
                        comunicarte con tu vendedor de confianza para finalizar
                      </b>{' '}
                      el proceso de compra. De lo contrario, tener en cuenta que
                      la reserva <b>será cancelada</b> automáticamente{' '}
                      <b>
                        el día {formatDate(bookingDetails?.deadline_date)} a las
                        22 hs.
                      </b>
                    </Typography>
                  ) : (
                    <Typography sx={sxStyle.bookingDetailText}>
                      Deberá{' '}
                      <b>{`dirigirse al ${name} seleccionado para finalizar`}</b>{' '}
                      el proceso de compra. De lo contrario, tener en cuenta que
                      la reserva <b>será cancelada</b> automáticamente{' '}
                      <b>
                        el día {sumarUnDia(bookingDetails?.created_at)} a las 22
                        hs.
                      </b>
                    </Typography>
                  )}
                </Grid>
              )}
              <Grid item container sx={sxStyle.gridBookingDetail}>
                {bookingDetails && (
                  <Grid
                    item
                    xs={12}
                    sx={
                      hasEntity
                        ? sxStyle.gridItemBookingDetailHotel
                        : sxStyle.gridItemBookingDetailOnlyHotel
                    }
                  >
                    <Typography sx={sxStyle.bookingDetailText}>
                      <Typography component="span" sx={{ fontWeight: 600 }}>
                        Alojamiento
                      </Typography>
                    </Typography>
                    <Typography sx={sxStyle.bookingDetailText}>
                      {bookingDetails?.hotel?.name}
                    </Typography>{' '}
                    {!!bookingDetails?.check_in && (
                      <Typography sx={sxStyle.bookingDetailText}>
                        Fecha de entrada: {formatDate(bookingDetails?.check_in)}
                      </Typography>
                    )}
                    {!!bookingDetails?.check_out && (
                      <Typography sx={sxStyle.bookingDetailText}>
                        Fecha de salida: {formatDate(bookingDetails?.check_out)}
                      </Typography>
                    )}
                  </Grid>
                )}

                <EntityInfo
                  bookingDetails={bookingDetails}
                  getHref={getHref}
                  sxStyle={sxStyle}
                  hasEntity={hasEntity}
                />
              </Grid>
            </Grid>
            <CloseButton history={history} />
          </Grid>
        </Grid>
      </Grid>
    </GridMainContainer>
  );
};

export default Hotels;
